// §V2 EXACT PORT — the spike's SETTINGS screen (docs/design-spikes/
// pomela-platform-v2/index.html lines 1002-1083): centered wrap-settings
// column · settings-head · sticky seg-nav (3 section pills + scroll-spy) ·
// Account / Preferences / System sections · appearance segtabs · REAL
// push-notification switch · Language & layout flip · Security / Privacy /
// Danger panels · invite gradhero (the one sanctioned gradient moment).
//
// Behavior is REAL, ported from src/screens/Settings.tsx:
//  - appearance = getThemePref/setThemePref (light/dark/system)
//  - push row = the real Notification permission + local-mute flow
//    (the spike's "Product emails" / "Weekly digest" switches are OMITTED —
//    there is no email-preference backend, and a dead switch would be fake)
//  - language row = real en↔he locale flip (setLocale + reload)
//  - identity/billing/danger rows route to the real screens (#account,
//    #upgrade); Security/Privacy open the legal pages (/legal/security, /legal/privacy)
//  - invite link = the same deterministic device-derived code the sidebar
//    InviteCard shows (shared localStorage cache, same hash)
import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { useAuth, useToast } from '@vibeshell/hooks';
import { Ic, tt } from './icons';
import { getThemePref, setThemePref, type ThemePref } from '../lib/theme';
import { getLocale, setLocale, t, type Locale } from '../lib/i18n';
import { getDeviceId } from '../lib/deviceId';

// ─── invite link — SAME derivation as components/InviteCard.tsx, so this
//     hero and the sidebar card always show one identical link ─────────────
const LS_CODE = 'pomela:invite:code';
const LS_SENT = 'pomela:invite:sent';

function codeFrom(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const raw = Math.abs(h).toString(36) + Math.abs(h >> 8).toString(36);
  const clean = raw.replace(/[01ilo]/g, 'p').toUpperCase();
  return clean.slice(0, 6);
}

function readInviteCode(): string {
  let code = '';
  try {
    code = localStorage.getItem(LS_CODE) || '';
  } catch {
    /* localStorage blocked — derive ephemerally below */
  }
  if (!code) {
    let deviceId = '';
    try {
      deviceId = localStorage.getItem('vs:creator:deviceId') || '';
    } catch {
      /* blocked */
    }
    code = codeFrom(deviceId || `${Date.now()}-${Math.random()}`);
    try {
      localStorage.setItem(LS_CODE, code);
    } catch {
      /* swallow */
    }
  }
  return code;
}

function bumpSent(): void {
  try {
    const next = (Number(localStorage.getItem(LS_SENT) || '0') || 0) + 1;
    localStorage.setItem(LS_SENT, String(next));
  } catch {
    /* the counter is a nicety — never block the share on storage */
  }
}

/** Seg-nav pills — the spike's exact three, targeting the section ids. */
const SECTIONS = [
  { id: 'stg-account', icon: 'user', key: 'v2.set.account', en: 'Account' },
  { id: 'stg-prefs', icon: 'sun', key: 'v2.set.prefs', en: 'Preferences' },
  { id: 'stg-system', icon: 'shield', key: 'v2.set.system', en: 'System' }
] as const;

export function SettingsV2(): JSX.Element {
  const { session } = useAuth();
  const toast = useToast();
  const screenRef = useRef<HTMLElement>(null);
  const [theme, setTheme] = useState<ThemePref>(getThemePref());
  const [active, setActive] = useState<string>('stg-account');
  const [notifyState, setNotifyState] = useState<NotificationPermission | 'unsupported'>(
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission
  );
  // A locally-remembered mute (same flow as screens/Settings.tsx): the browser
  // permission can't be revoked from JS, so "off" = muted + push unsubscribed.
  const [pushMuted, setPushMuted] = useState<boolean>(() => {
    try {
      return localStorage.getItem('vs:creator:push-muted') === '1';
    } catch {
      return false;
    }
  });
  const [inviteCode, setInviteCode] = useState('');
  useEffect(() => setInviteCode(readInviteCode()), []);

  const locale: Locale = getLocale();
  const pushOn = notifyState === 'granted' && !pushMuted;
  const inviteUrl = inviteCode ? `https://pomela.app/?r=${inviteCode}` : 'https://pomela.app';

  // §V2 scroll reveal — .sr blocks rise in as they enter the #canvas viewport
  // (spike studio.js: threshold .12, rootMargin '0px 0px -6%', reveal once).
  useEffect(() => {
    const scope = screenRef.current;
    if (!scope) return;
    const els = Array.from(scope.querySelectorAll<HTMLElement>('.sr:not(.in)'));
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('in');
            io.unobserve(en.target);
          }
        });
      },
      { root: document.getElementById('canvas'), threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    const raf = requestAnimationFrame(() => els.forEach((el) => io.observe(el)));
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
    };
  }, []);

  // §V2 scroll-spy — the active pill follows the section in the reading band
  // of #canvas. Deterministic: the TOP-MOST intersecting section wins.
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return;
    const order: string[] = SECTIONS.map((s) => s.id);
    const io = new IntersectionObserver(
      (entries) => {
        const hits = entries
          .filter((e) => e.isIntersecting)
          .map((e) => (e.target as HTMLElement).id)
          .sort((a, b) => order.indexOf(a) - order.indexOf(b));
        if (hits.length) setActive(hits[0]!);
      },
      { root: document.getElementById('canvas'), rootMargin: '-15% 0px -75% 0px', threshold: 0 }
    );
    order.forEach((id) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  // Pill click: light immediately + smooth-scroll; the spy keeps it honest.
  function jumpTo(id: string): void {
    setActive(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function chooseTheme(pref: ThemePref, label: string): void {
    setTheme(pref);
    setThemePref(pref);
    toast.show(tt('v2.set.themeToast', 'Appearance set to {mode}').replace('{mode}', label), { variant: 'success' });
  }

  // Real language + RTL flip — the existing i18n layer (he ⇒ RTL); reload
  // applies the new locale + document direction app-wide (as screens/Settings).
  function flipLocale(): void {
    const next: Locale = locale === 'he' ? 'en' : 'he';
    setLocale(next);
    location.reload();
  }

  // REAL push permission + mute flow — ported verbatim from screens/Settings.
  async function setPush(on: boolean): Promise<void> {
    if (typeof Notification === 'undefined') return;
    if (on) {
      let perm: NotificationPermission | 'unsupported' = notifyState === 'unsupported' ? 'default' : notifyState;
      if (perm === 'default') {
        perm = await Notification.requestPermission();
        setNotifyState(perm);
      }
      if (perm === 'granted') {
        setPushMuted(false);
        try {
          localStorage.removeItem('vs:creator:push-muted');
        } catch {
          /* storage blocked — state still reflects the change this session */
        }
        toast.show(tt('set.pushOnToast', 'Push notifications on.'), { variant: 'success' });
      } else if (perm === 'denied') {
        toast.show(tt('set.pushBlockedToast', 'Your browser is blocking notifications. Enable them in site settings.'), { variant: 'error' });
      }
    } else {
      // Off: remember the mute and stop delivery by unsubscribing any push
      // subscription. Full OS-level reset stays in the browser's site settings.
      setPushMuted(true);
      try {
        localStorage.setItem('vs:creator:push-muted', '1');
      } catch {
        /* storage blocked — session state still flips the switch off */
      }
      try {
        const reg = await navigator.serviceWorker?.ready;
        const sub = await reg?.pushManager?.getSubscription();
        await sub?.unsubscribe();
      } catch {
        /* best-effort — no active subscription is the common, fine case */
      }
      toast.show(tt('set.pushOffToast', 'Push notifications off.'), { variant: 'success' });
    }
  }

  const shareText = tt('invite.shareText', 'I’ve been building little apps on my phone in about 90 seconds with Pomela. Try it:');

  async function copyInvite(): Promise<void> {
    navigator.vibrate?.(6);
    try {
      await navigator.clipboard?.writeText(`${shareText}\n${inviteUrl}`);
      bumpSent();
      toast.show(tt('v2.set.copiedToast', 'Link copied ✓'), { variant: 'success' });
    } catch {
      toast.show(tt('invite.copyFail', 'Copy failed. Long-press the link instead.'), { variant: 'error' });
    }
  }

  async function shareInvite(): Promise<void> {
    navigator.vibrate?.(8);
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: tt('invite.shareTitle', 'Build an app with Pomela'), text: shareText, url: inviteUrl });
        bumpSent();
        return;
      } catch {
        /* user cancelled — fall through to copy */
      }
    }
    await copyInvite();
  }

  const pushSub =
    notifyState === 'unsupported' ? tt('set.pushUnsupported', 'Not supported on this browser')
      : notifyState === 'denied' ? tt('set.pushBlocked', 'Blocked in your browser settings')
        : pushOn ? tt('set.pushOn', 'On: installed apps can send you reminders')
          : notifyState === 'granted' ? tt('set.pushOffMuted', 'Off: muted on this device. Reset fully in site settings.')
            : tt('set.pushDefault', 'Let installed apps send you reminders');

  const initial = (session.email?.[0] || 'P').toUpperCase();

  return (
    <section className="screen on" data-screen="settings" ref={screenRef}>
      <div className="wrap-settings">
        <div className="settings-head">
          <h2 className="title-page">{tt('v2.set.title', 'Settings')}</h2>
          <p className="sub-h">{tt('v2.set.sub', 'Your account, preferences and system controls, all in one place.')}</p>
        </div>

        {/* section nav is a sticky horizontal segmented control, not a left sidebar */}
        <nav className="seg-nav" id="settingsNav" aria-label={tt('v2.set.sectionsAria', 'Settings sections')}>
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`seg-nav-i${active === s.id ? ' on' : ''}`}
              data-scrollto={`#${s.id}`}
              aria-current={active === s.id ? 'true' : undefined}
              onClick={() => jumpTo(s.id)}
            >
              <Ic n={s.icon} /><span>{tt(s.key, s.en)}</span>
            </button>
          ))}
        </nav>

        <div className="stack" style="gap:30px">
          <section className="stg-section sr" id="stg-account">
            <span className="stg-eyebrow">{tt('v2.set.account', 'Account')}</span>
            <div className="stack">
              <div className="panel">
                <button type="button" className="lrow clk" onClick={() => { location.hash = 'account'; }}>
                  <span className="ava">{initial}</span>
                  <div className="l-txt">
                    <b>{session.signedIn ? (session.email ?? tt('set.signedIn', 'Signed in')) : tt('set.notSignedIn', 'Not signed in')}</b>
                    <span>{session.signedIn ? tt('set.manageAccount', 'Manage your account') : tt('set.signInSync', 'Sign in to sync across devices')}</span>
                  </div>
                  <span className="l-trail"><Ic n="chev" /></span>
                </button>
              </div>
              <div className="panel">
                <button type="button" className="lrow clk" onClick={() => { location.hash = 'upgrade'; }}>
                  <span className="l-ico brand"><Ic n="spark" /></span>
                  <div className="l-txt">
                    <b>{tt('v2.set.billing', 'Billing & credits')}</b>
                    <span>
                      {session.plan === 'pro'
                        ? tt('v2.set.billingProSub', 'Pro plan · manage your credits')
                        : `${tt('set.planFree', 'Free plan')} · ${tt('set.planUpgrade', 'Upgrade to Pro for more monthly credits')}`}
                    </span>
                  </div>
                  <span className="l-trail">
                    {session.plan === 'pro' && <span className="badge pro">PRO</span>}
                    <Ic n="chev" />
                  </span>
                </button>
              </div>
            </div>
          </section>

          <section className="stg-section sr" id="stg-prefs">
            <span className="stg-eyebrow">{tt('v2.set.prefs', 'Preferences')}</span>
            <div className="stack">
              <div className="panel panel-p" id="stg-appearance">
                <span className="sec-label" style="display:block;margin-bottom:12px">{tt('v2.set.appearance', 'Appearance')}</span>
                <div className="segtabs block" id="appearanceSeg">
                  {([
                    { id: 'light', icon: 'sun', key: 'v2.set.light', en: 'Light' },
                    { id: 'dark', icon: 'moon', key: 'v2.set.dark', en: 'Dark' },
                    { id: 'system', icon: 'phone', key: 'v2.set.system', en: 'System' }
                  ] as const).map((tb) => (
                    <button
                      key={tb.id}
                      type="button"
                      className={`segtab${theme === tb.id ? ' on' : ''}`}
                      data-theme-pick={tb.id}
                      aria-pressed={theme === tb.id}
                      onClick={() => chooseTheme(tb.id, tt(tb.key, tb.en))}
                    >
                      <Ic n={tb.icon} />{tt(tb.key, tb.en)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="panel">
                <div className="panel-hd"><span className="sec-label">{tt('v2.set.notifications', 'Notifications')}</span></div>
                <div className="panel-p">
                  {/* REAL push row. The spike's "Product emails" / "Weekly digest"
                      switches are omitted — no email-pref backend exists, and a
                      switch that saves nothing would be a fake control. */}
                  <div className="ep-row" style="border:none;padding-top:0">
                    <div className="er-txt">
                      <b>{tt('set.pushLabel', 'Push notifications')}</b>
                      <span>{pushSub}</span>
                    </div>
                    {notifyState !== 'unsupported' && notifyState !== 'denied' && (
                      <span
                        className={`switch${pushOn ? ' on' : ''} mla`}
                        role="switch"
                        aria-checked={pushOn}
                        aria-label={tt('set.pushLabel', 'Push notifications')}
                        tabIndex={0}
                        onClick={() => void setPush(!pushOn)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            void setPush(!pushOn);
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="panel">
                <button type="button" className="lrow clk" id="settingsRtlLink" onClick={flipLocale}>
                  <span className="l-ico brand"><Ic n="globe" /></span>
                  <div className="l-txt">
                    <b>{tt('v2.set.language', 'Language & layout')}</b>
                    <span>{locale === 'he' ? tt('v2.set.dirRtl', 'עברית · right-to-left') : tt('v2.set.dirLtr', 'English · left-to-right')}</span>
                  </div>
                  <span className="l-trail">{tt('v2.set.tapFlip', 'Tap to flip')} <Ic n="chev" /></span>
                </button>
              </div>
            </div>
          </section>

          <section className="stg-section sr" id="stg-system">
            <span className="stg-eyebrow">{tt('v2.set.systemSection', 'System')}</span>
            <div className="stack">
              <div className="panel">
                <button type="button" className="lrow clk" onClick={() => window.open('/legal/security', '_blank')}>
                  <span className="l-ico"><Ic n="shield" /></span>
                  <div className="l-txt">
                    <b>{tt('v2.set.security', 'Security')}</b>
                    {/* honest copy — the page is our security-practices statement;
                        Pomela has no 2FA/session-manager to promise */}
                    <span>{tt('v2.set.securitySub', 'How Pomela protects your account and data')}</span>
                  </div>
                  <span className="l-trail"><Ic n="chev" /></span>
                </button>
              </div>
              <div className="panel">
                <button type="button" className="lrow clk" onClick={() => window.open('/legal/privacy', '_blank')}>
                  <span className="l-ico"><Ic n="lock" /></span>
                  <div className="l-txt">
                    <b>{tt('v2.set.privacy', 'Privacy')}</b>
                    <span>{tt('v2.set.privacySub', 'Data, tracking and exports')}</span>
                  </div>
                  <span className="l-trail"><Ic n="chev" /></span>
                </button>
              </div>
              <div className="panel">
                {/* the REAL GDPR export (legacy Settings row) — the delete
                    dialog and the privacy page both point users here */}
                <button
                  type="button"
                  className="lrow clk"
                  onClick={() => window.open(`/v1/me/export?deviceId=${encodeURIComponent(getDeviceId())}`, '_blank')}
                >
                  <span className="l-ico"><Ic n="download" /></span>
                  <div className="l-txt">
                    <b>{t('set.export')}</b>
                    <span>{t('set.exportSub')}</span>
                  </div>
                  <span className="l-trail"><Ic n="chev" /></span>
                </button>
              </div>
              <div className="panel" style="background:color-mix(in srgb,var(--danger) 6%,var(--surface))">
                <button type="button" className="lrow clk" onClick={() => { location.hash = 'account'; }}>
                  <span className="l-ico" style="background:color-mix(in srgb,var(--danger) 13%,transparent);color:var(--danger)"><Ic n="trash" /></span>
                  <div className="l-txt">
                    <b style="color:var(--danger)">{tt('v2.set.danger', 'Danger zone')}</b>
                    <span>{tt('v2.set.dangerSub', 'Sign out or delete your data')}</span>
                  </div>
                  <span className="l-trail"><Ic n="chev" /></span>
                </button>
              </div>
            </div>
          </section>

          {/* invite promo demoted below the user's real controls (single sanctioned gradient moment) */}
          <div className="gradhero sr">
            <span className="gh-ey">{tt('invite.eyebrow', 'Spread the joy')}</span>
            <h3>{tt('invite.title', 'Invite a friend to build with Pomela.')}</h3>
            <p>{tt('v2.set.inviteSub', 'Pomela gets better as more people build with it. Send your link, your friend lands straight in the builder, ready to make their first app.')}</p>
            <div className="gh-link">
              <span className="gh-field"><span className="k">{tt('v2.set.yourLink', 'YOUR LINK')}</span>{inviteUrl}</span>
              <button type="button" className="btn btn-solid" onClick={() => void shareInvite()}><Ic n="share" /> {tt('invite.share', 'Share invite')}</button>
              <button type="button" className="btn on-grad-chip" onClick={() => void copyInvite()}><Ic n="copy" /> {tt('v2.set.copy', 'Copy')}</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
